import { View } from "ui/atoms/view";
import { Switch, Text } from "react-native";
import {
  AssessmentEditFormModel,
  useAssessmentEdit,
} from "../context/AssessmentEditContext";
import clsx from "clsx";
import { FormControlledDropdown } from "ui/molecules/form/FormControlledDropdown";
import {
  FormEnums,
  FormTagDescription,
  FormTagTypes,
} from "app/services/api/types/forms/enums/FormEnums";
import { FormControlledInput } from "ui/molecules/form/FormControlledInput";
import { Controller, useWatch } from "react-hook-form";
import { Icon, InfoIcon } from "ui/gluestack/icon";

interface Props {
  className?: string;
}
export function AssessmentEditDetailsOrganism({ className }: Props) {
  const { control, isEditing, hasEditingPermission } = useAssessmentEdit();

  const tag = useWatch<AssessmentEditFormModel>({ control, name: "tag" });

  return (
    <View className={clsx("gap-4", className)}>
      <Text className="font-semibold text-lg">Details</Text>

      <View className={"gap-6 border p-4 rounded border-gray-200"}>
        <View>
          <FormControlledInput
            control={control}
            name="name"
            id="name"
            isRequired
            label="Assessment Name"
            placeholder="Enter an assessment name"
            isDisabled={!hasEditingPermission}
          />
        </View>
        <View>
          <FormControlledDropdown
            control={control}
            name="tag"
            options={FormTagTypes}
            label="Assessment Tag"
            isRequired
            isDisabled={isEditing || !hasEditingPermission}
            placeholder="Select an assessment tag"
          />

          <View className="flex flex-row gap-1 mt-2 items-center">
            <Icon as={InfoIcon} width={8} height={8} color="gray" />
            <Text className="text-xs text-gray-600">
              {tag
                ? FormTagDescription[tag as FormEnums]
                : "The assessment tag will determine where the report is shown"}
            </Text>
          </View>
        </View>

        <View className="flex flex-row items-center justify-between max-w-[500px]">
          <Text className="text-sm text-typography-black">
            Can the assessment have a draft status?
          </Text>
          <Controller
            control={control}
            name={"can_have_draft_status"}
            render={({ field: { value, onChange } }) => (
              <Switch
                value={!!value}
                onValueChange={onChange}
                disabled={!hasEditingPermission}
              />
            )}
          />
        </View>

        <View className="flex flex-row items-center justify-between max-w-[500px]">
          <Text className="text-sm text-typography-black">
            Does this assessment have a scoring?
          </Text>
          <Controller
            control={control}
            name={"has_scoring"}
            render={({ field: { value, onChange } }) => (
              <Switch
                value={!!value}
                onValueChange={onChange}
                disabled={!hasEditingPermission}
              />
            )}
          />
        </View>
        <View className="flex flex-row items-center justify-between max-w-[500px]">
          <Text className="text-sm text-typography-black">
            Is this assessment password protected?
          </Text>
          <Controller
            control={control}
            name={"password_protected"}
            render={({ field: { value, onChange } }) => (
              <Switch
                value={!!value}
                onValueChange={onChange}
                disabled={!hasEditingPermission}
              />
            )}
          />
        </View>

        <View className="flex flex-row items-center justify-between max-w-[500px]">
          <Text className="text-sm text-typography-black">
            Do you want to require a signoff on this assessment?
          </Text>
          <Controller
            control={control}
            name={"requires_signoff"}
            render={({ field: { value, onChange } }) => (
              <Switch
                value={!!value}
                onValueChange={onChange}
                disabled={!hasEditingPermission}
              />
            )}
          />
        </View>

        <View>
          <View className="flex flex-row items-center justify-between max-w-[500px]">
            <Text className="text-sm text-typography-black">
              Do you want to require an electronic signature on this assessment?
            </Text>
            <Controller
              control={control}
              name={"requires_electronic_signature"}
              render={({ field: { value, onChange } }) => (
                <Switch
                  value={!!value}
                  onValueChange={onChange}
                  disabled={!hasEditingPermission}
                />
              )}
            />
          </View>
          <View className="flex flex-row gap-1 mt-2 items-center">
            <Icon as={InfoIcon} width={8} height={8} color="gray" />
            <Text className="text-xs text-gray-600">
              This option will not effect the assessment if no signature inputs
              are on the form
            </Text>
          </View>
        </View>

        <View className="flex flex-row items-center justify-between max-w-[500px]">
          <Text className="text-sm text-typography-black">
            Do you want to split this assessment into pages?
          </Text>
          <Controller
            control={control}
            name={"has_pages"}
            render={({ field: { value, onChange } }) => (
              <Switch
                value={!!value}
                onValueChange={onChange}
                disabled={!hasEditingPermission}
              />
            )}
          />
        </View>
      </View>
    </View>
  );
}
